import { Injectable } from '@angular/core';
import {
  Observable,
  Subject,
  catchError,
  finalize,
  map,
  merge,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { AppEventsService } from 'src/app/services/app-events.service';
import { ApiService } from '../api/api.service';
import { StorageKeys, StorageService } from '../services/storage.service';
import { CUSTOM_CONTEXT, HttpMethods } from '../api/api.enums';
import {
  LoginReq,
  AuthData,
  SignupReq,
  RefreshTokenReq,
  ForgottenPasswordReq,
  ChangePasswordReq,
} from '../api/contracts/auth';
import { NavController } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { HttpContext, HttpContextToken } from '@angular/common/http';
import { AccessControlData } from '../api/contracts/client';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private isTokenRefreshingInProgress = false;
  private readonly tokenRefreshed$ = new Subject<AuthData>();

  readonly isAuthed$: Observable<boolean>;
  public readonly refreshTokenUrl: string = `${environment.apiUrl}/auth/token/refresh`;

  constructor(
    private readonly apiService: ApiService,
    private readonly storageService: StorageService,
    private readonly eventService: AppEventsService,
    private readonly navCtrl: NavController
  ) {
    this.isAuthed$ = merge(
      this.getStoredAuthData().pipe(map((authData) => !!authData)),
      this.eventService.afterLogin.pipe(map(() => true)),
      this.eventService.afterLogout.pipe(map(() => false))
    );
  }

  login(credentials: LoginReq): Observable<AuthData> {
    return this.apiService
      .request<AuthData, null, LoginReq>({
        method: HttpMethods.POST,
        url: `${environment.apiUrl}/auth/login`,
        body: credentials,
        context: new HttpContext().set(
          CUSTOM_CONTEXT.SKIP_AUTH_INTERCEPTOR,
          true
        ),
      })
      .pipe(
        map((res) => res.body!),
        switchMap((authData) =>
          this.storageService.set(StorageKeys.AUTH_DATA, authData)
        ),
        tap((authData) => this.eventService.afterLogin.next(authData))
      );
  }

  signup(body: SignupReq): Observable<void> {
    return this.apiService
      .request<void, null, SignupReq>({
        method: HttpMethods.POST,
        url: `${environment.apiUrl}/auth/registration`,
        body,
      })
      .pipe(map((res) => res.body!));
  }

  refreshToken(body: RefreshTokenReq): Observable<AuthData> {
    if (this.isTokenRefreshingInProgress) {
      return this.tokenRefreshed$.asObservable().pipe(take(1));
    }
    this.isTokenRefreshingInProgress = true;
    return this.apiService
      .request<AuthData, null, RefreshTokenReq>({
        method: HttpMethods.POST,
        url: `${environment.apiUrl}/auth/token/refresh`,
        body: body,
        context: new HttpContext().set(
          CUSTOM_CONTEXT.SKIP_AUTH_INTERCEPTOR,
          true
        ),
      })
      .pipe(
        catchError((err) => {
          return throwError(() => err);
        }),
        map((res) => res.body!),
        switchMap((authData) =>
          this.storageService.set(StorageKeys.AUTH_DATA, authData)
        ),
        tap((authData) => {
          this.tokenRefreshed$.next(authData);
          this.isTokenRefreshingInProgress = false;
        })
      );
  }

  sendForgottenPasswordRequest(
    body: ForgottenPasswordReq
  ): Observable<unknown> {
    return this.apiService
      .request<null, null, ForgottenPasswordReq>({
        method: HttpMethods.POST,
        url: `${environment.apiUrl}/auth/forgotten_password`,
        body,
      })
      .pipe(map((res) => res.body!));
  }

  changePassword(body: ChangePasswordReq): Observable<void> {
    return this.apiService
      .request<null, null, ChangePasswordReq>({
        method: HttpMethods.POST,
        url: `${environment.apiUrl}/auth/change_password`,
        body,
      })
      .pipe(map((res) => res.body!));
  }

  getStoredAuthData(): Observable<AuthData | null> {
    return this.storageService.get<AuthData>(StorageKeys.AUTH_DATA);
  }

  logout(): Observable<void> {
    this.eventService.afterLogout.next();
    return this.clearStoredAuthData().pipe(
      tap(() => {
        this.navCtrl.navigateRoot('login');
      })
    );
  }

  clearStoredAuthData(): Observable<void> {
    return this.storageService.remove(StorageKeys.AUTH_DATA);
  }

  isStoredTokenExpired(): Observable<boolean> {
    return this.getStoredAuthData().pipe(
      map((storedAuthData) =>
        storedAuthData
          ? storedAuthData.refreshTokenExpiration <= Date.now() / 1000
          : true
      )
    );
  }

  retrieveAccessControlData(): Observable<AccessControlData> {
    return this.apiService
      .request<AccessControlData>({
        method: HttpMethods.GET,
        url: `${environment.apiUrl}/auth/auth_code_data`,
      })
      .pipe(map((res) => res.body as AccessControlData));
  }
}
